<template>
  <d-container fluid class="main-content-container px-4">
    <d-row no-gutters class="page-header py-4">
      <div class="col-12 text-center text-sm-left mb-0">
        <span class="text-uppercase page-subtitle d-block">{{
          $t('word.edit.subtitle')
        }}</span>
        <h3 class="page-title d-inline">
          <span class="text-primary">
            {{ word ? word.word : '...' }}
          </span>
        </h3>
        <router-link
          class="btn btn-secondary text-white float-right"
          :to="{ name: 'admin-word-list', params: { page: 1 } }"
        >
          {{ $t('shared.actions.back') }}
        </router-link>
      </div>
    </d-row>

    <d-row>
      <d-col>
        <d-card class="card-small mb-4">
          <div
            class="card-header border-bottom d-flex justify-content-between align-items-center"
          >
            <div class="col-6">
              <h6 class="m-0">{{ $t('word.edit.questionsTitle') }}</h6>
            </div>

            <div class="col-2 d-flex">
              <div class="input-group">
                <d-select
                  :key="filterSelectKey"
                  :value="filters.level"
                  :options="levelFilters"
                  value-field="slug"
                  text-field="label"
                  :disabled="isLoading"
                  @change="onLevelFilterChange"
                />
              </div>
            </div>

            <div class="col-2 text-right">
              <button
                class="btn btn-success btn-sm text-white"
                :disabled="isLoading"
                @click="addNewRow"
              >
                <i class="fas fa-plus mr-1" /> {{ $t('word.edit.addQuestion') }}
              </button>
            </div>
          </div>

          <div class="card-body p-0 pb-3 text-left">
            <div class="table-overflow">
              <question-table
                :questions="questionsList"
                :word-id="word ? word.id : null"
                :levels="levels"
                @saved="onSaved"
                @deleted="onDeleted"
                @add-row="addNewRow"
              />
            </div>
          </div>
        </d-card>
      </d-col>
    </d-row>

    <confirmation-modal
      :cancel="closeDeleteModal"
      :confirm="executeDeleteQuestion"
      :title="$t('word.delete.questionTitle')"
      :message="$t('word.delete.questionMessage')"
    />
  </d-container>
</template>

<script>
import { mapActions } from 'vuex';
import { LEVEL, LEVEL_OPTIONS } from '_utils/level';
import WordService from '_services/rest/modules/word';
import PuzzleService from '_services/rest/modules/puzzle';
import ConfirmationModal from '_shared/ConfirmationModal.vue';
import QuestionTable from '../components/QuestionTable.vue';

export default {
  name: 'question-list',
  components: { ConfirmationModal, QuestionTable },
  data() {
    return {
      word: null,
      questionsList: [],
      questionToDelete: null,
      filters: {
        level: null,
      },
      filterSelectKey: 0,
      isLoading: false,
    };
  },
  computed: {
    levels() {
      return LEVEL_OPTIONS.map(o => ({
        slug: o.slug,
        label: this.$t(o.labelKey),
      }));
    },
    levelFilters() {
      return [
        { slug: null, label: this.$t('word.edit.levelFilter') },
        ...this.levels,
      ];
    },
  },
  methods: {
    ...mapActions(['setToggle', 'activeAlert']),
    fetchWordData(id) {
      this.isLoading = true;

      WordService.getWordById(id, this.filters)
        .then(response => {
          this.word = response;
          this.questionsList = response.questions
            ? [...response.questions]
            : [];
        })
        .catch(error => {
          this.activeAlert({
            message:
              (error && error.message) || this.$t('word.alert.loadWordError'),
            theme: 'danger',
          });
          if (error && Number(error.status) === 404) {
            this.$router.push({ name: 'admin-word-list' });
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    addNewRow() {
      if (!this.word) return;
      this.questionsList.push({
        id: null,
        word_id: this.word.id,
        question: '',
        size_id: 3,
        level: this.filters.level || LEVEL.MEDIUM,
        isNew: true,
      });
    },
    onSaved(savedRow, response) {
      savedRow.id = response.id;
      savedRow.isNew = false;
    },
    removeQuestionFromList(row) {
      const idx = this.questionsList.findIndex(
        q => q === row || (row.id != null && q.id === row.id),
      );
      if (idx !== -1) {
        this.questionsList.splice(idx, 1);
      }
    },
    onDeleted(row) {
      this.questionToDelete = row;
      if (!row.id) {
        this.removeQuestionFromList(row);
        this.questionToDelete = null;
        return;
      }
      this.setToggle({ type: 'confirm_modal', bool: true });
    },
    closeDeleteModal() {
      this.setToggle({ type: 'confirm_modal', bool: false });
      this.clearDeleteState();
    },
    clearDeleteState() {
      this.questionToDelete = null;
    },
    executeDeleteQuestion() {
      if (!this.questionToDelete) return;
      PuzzleService.deleteQuestion(this.questionToDelete.id)
        .then(() => {
          this.removeQuestionFromList(this.questionToDelete);
          this.activeAlert({
            message: this.$t('word.alert.questionDeleted'),
            theme: 'success',
          });
          this.closeDeleteModal();
        })
        .catch(() => {
          this.activeAlert({
            message: this.$t('word.alert.questionDeleteError'),
            theme: 'danger',
          });
          this.closeDeleteModal();
        });
    },
    onLevelFilterChange(level) {
      const hasDraft = this.questionsList.some(q => q.isNew || q.isEditing);
      if (hasDraft) {
        this.filterSelectKey += 1;
        this.activeAlert({
          message: this.$t('word.alert.saveOrCancelBeforeFilter'),
          theme: 'warning',
        });
        return;
      }
      this.filters.level = level;
      if (this.word?.id) {
        this.fetchWordData(this.word.id);
      }
    },
  },
  mounted() {
    if (this.$route.params.wordId) {
      this.fetchWordData(this.$route.params.wordId);
    }
  },
};
</script>
